import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
  type ShouldRevalidateFunction,
} from "react-router-dom";
import { INTENTS, loadCookiesData } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";
import {
  apiClient,
  UnitPlanDetailsLoaderResult,
} from "~/services/apiClient";

export type UnitPlanDetailsPageTab = "details" | "linkedUnits";

/** Tab switches only change `?tab=`; data is already in loaderData. */
function onlyUnitPlanTabQueryChanged(current: URL, next: URL): boolean {
  if (current.pathname !== next.pathname) return false;
  if (current.search === next.search) return false;

  const currentParams = new URLSearchParams(current.search);
  const nextParams = new URLSearchParams(next.search);
  const keys = new Set<string>();
  currentParams.forEach((_, key) => keys.add(key));
  nextParams.forEach((_, key) => keys.add(key));

  for (const key of Array.from(keys)) {
    if (key === "tab") continue;
    if ((currentParams.get(key) ?? "") !== (nextParams.get(key) ?? "")) {
      return false;
    }
  }

  return (currentParams.get("tab") ?? "") !== (nextParams.get("tab") ?? "");
}

export const unitPlanDetailsShouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}) => {
  if (onlyUnitPlanTabQueryChanged(currentUrl, nextUrl)) {
    return false;
  }
  return defaultShouldRevalidate;
};

export const UnitPlanDetailsPageLoader = async ({
  params,
}: LoaderFunctionArgs): Promise<UnitPlanDetailsPageLoaderType | Response> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const unitPlanID = params.unitPlanID || "";
  const propertyID = params.propertyID || "";

  const result = await apiClient.loader<UnitPlanDetailsLoaderResult>(
    "unit-plan-details",
    { id: unitPlanID, ownerID, ownerType, userID: user.uid },
  );

  if (!result.success || !result.data) {
    throw new Error(result.error ?? "Failed to load unit plan details");
  }

  const data = result.data;
  if (propertyID && data.propertyDetails.id !== propertyID) {
    return redirect(`/property/${data.propertyDetails.id}`);
  }

  const files = await S3ServiceLambda.list({
    entityType: APITypes.EntityType.UnitPlan,
    entityID: unitPlanID,
  });

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  return {
    planDetails: data.planDetails,
    propertyDetails: data.propertyDetails,
    linkedUnits: data.linkedUnits ?? [],
    unitsAvailableToLink: data.unitsAvailableToLink ?? [],
    buildingMap: data.buildingMap ?? {},
    ownerID,
    ownerType,
    files: files.data,
    canReadUnitPlan: isAdminOrSuperAdmin || data.permissions.canReadUnitPlan,
    canEditUnitPlan: isAdminOrSuperAdmin || data.permissions.canEditUnitPlan,
    canDeleteUnitPlan: isAdminOrSuperAdmin || data.permissions.canDeleteUnitPlan,
    isAdminOrSuperAdmin,
  };
};

export type UnitPlanDetailsPageLoaderType = {
  planDetails: APITypes.UnitPlan;
  propertyDetails: APITypes.Property;
  linkedUnits: APITypes.Unit[];
  unitsAvailableToLink: APITypes.Unit[];
  buildingMap: Record<string, APITypes.Building>;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  files: Awaited<ReturnType<typeof S3ServiceLambda.list>>["data"];
  canReadUnitPlan: boolean;
  canEditUnitPlan: boolean;
  canDeleteUnitPlan: boolean;
  isAdminOrSuperAdmin: boolean;
};

export const UnitPlanDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success || !authAccessInfo.data) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const propertyID = params.propertyID || "";
  const unitPlanID = params.unitPlanID || "";
  const linkedUnitsTab = `?tab=linkedUnits`;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const unitPlanCacheTargets = [
    { entity: "unit-plan" as const, entityID: unitPlanID },
  ];
  const mutateUnitPlan = <T,>(fn: () => Promise<T>) =>
    withEntityCacheInvalidation(unitPlanCacheTargets, fn);
  const mutateUnitPlanAndUnits = <T,>(fn: () => Promise<T>) =>
    withEntityCacheInvalidation(
      [...unitPlanCacheTargets, { entity: "unit" as const }],
      fn,
    );

  if (intent === INTENTS._edit_unit_plan) {
    return mutateUnitPlan(async () => {
      const rawBody = formData.get("body") as string | null;
      if (!rawBody) return {};
      const formValues = JSON.parse(rawBody) as Record<string, unknown>;
      const result = await apiClient.sendIntent<APITypes.UnitPlan>(
        INTENTS._edit_unit_plan,
        {
          ...formValues,
          unitPlanID,
          ownerID,
          userID: user.uid,
          isAdminOrSuperAdmin,
        },
      );
      if (!result.success) {
        return { success: false, error: result.error };
      }
      return redirect(`/property/${propertyID}/unit-plan/${unitPlanID}`);
    });
  }

  if (intent === INTENTS._remove_unit_plan) {
    return mutateUnitPlan(async () => {
      const result = await apiClient.sendIntent(INTENTS._remove_unit_plan, {
        unitPlanID,
        ownerID,
        userID: user.uid,
        isAdminOrSuperAdmin,
        _version: Number(formData.get("_version")),
      });
      if (!result.success) {
        return { success: false, error: result.error };
      }
      return redirect(`/property/${propertyID}?tab=unitPlans`);
    });
  }

  if (intent === "link_units_to_plan") {
    return mutateUnitPlanAndUnits(async () => {
      const rawBody = formData.get("body") as string | null;
      if (!rawBody) return { success: false, error: "Missing body" };
      const { unitIDs } = JSON.parse(rawBody) as { unitIDs?: string[] };
      const ids = unitIDs ?? [];
      for (const id of ids) {
        const result = await apiClient.sendIntent<APITypes.Unit>(INTENTS._edit_unit, {
          id,
          unitPlanID,
          userID: user.uid,
          isAdminOrSuperAdmin,
        });
        if (!result.success) {
          return { success: false, error: result.error };
        }
      }
      return redirect(
        `/property/${propertyID}/unit-plan/${unitPlanID}${linkedUnitsTab}`,
      );
    });
  }

  if (intent === "unlink_unit_from_plan") {
    return mutateUnitPlanAndUnits(async () => {
      const rawBody = formData.get("body") as string | null;
      if (!rawBody) return { success: false, error: "Missing body" };
      const { unitID, _version, numBedroom, numBathroom } = JSON.parse(
        rawBody,
      ) as {
        unitID?: string;
        _version?: number;
        numBedroom?: string;
        numBathroom?: string;
      };
      if (!unitID) return { success: false, error: "unitID is required" };
      const result = await apiClient.sendIntent<APITypes.Unit>(INTENTS._edit_unit, {
        id: unitID,
        unitPlanID: null,
        _version,
        numBedroom,
        numBathroom,
        userID: user.uid,
        isAdminOrSuperAdmin,
      });
      if (!result.success) {
        return { success: false, error: result.error };
      }
      return redirect(
        `/property/${propertyID}/unit-plan/${unitPlanID}${linkedUnitsTab}`,
      );
    });
  }

  return {};
};
